/**
 * `P0-UIK-018`: the `demo` flag, and the two switchers it gates.
 *
 * 4.7.3 defines it: *"The `demo` flag enables the mockups' simulation devices
 * (`Simuler une nouvelle demande client`, `Simuler l'événement suivant`, the
 * view and persona switchers). It is on in local and staging, off in
 * production, and a startup assertion fails the boot if it is on with
 * `NODE_ENV=production`."* `WF-CON-0` and `WF-BNK-01` each say the same thing
 * about their own switcher: it *"exists only behind the `demo` flag"*.
 *
 * **Danger sentence, and it is the reason this is two conditions and not one.**
 * The console's `Vue` switcher changes which screens a person can reach; the
 * bank's persona switcher moves an analyst into the chef's decisions queue and
 * credit policy. If either appears in production, the whitelist stops being a
 * property of the user and becomes a dropdown , every screen reachable by
 * everyone, and the audit trail says the right person did it.
 *
 * A single server-supplied boolean is not enough for that. A flag is a row in a
 * table (4.7.3), and rows are edited, copied between environments by a restore,
 * and set by whoever holds the admin route. So the build has a veto: **in a
 * production build the switchers are off no matter what the server says.** Two
 * independent things must be wrong before a demonstration device appears in
 * front of a customer, which is the same shape as 4.7.3's own boot assertion.
 *
 * **The flag is only ever read through `estDemo`.** A screen that reads the
 * server's boolean directly gets one of the two conditions, and the one it gets
 * is the one that can be edited.
 */

export interface DemoEntrees {
  /**
   * What the server says. 4.7.3 stores flags in the database and exposes them
   * *"to the frontend through `/api/v1/me`"*.
   *
   * `/me` does not carry flags yet , the surface exists (`/api/v1/admin/flags`)
   * and the `demo` row does not , so today every app passes `false` unless a
   * developer turns it on locally. When `/me` grows its flag map this reads it
   * and nothing else changes.
   */
  readonly serveur: boolean;
  /**
   * Whether this is a production BUILD. Injected rather than read from
   * `process.env` here: a package that reads the environment behaves
   * differently depending on who bundled it, and this value must be decided by
   * the build that ships, once.
   */
  readonly productionBuild: boolean;
}

/**
 * The only way to ask whether demonstration devices may render.
 *
 * Both conditions, and the production build wins. Written as an explicit
 * `if` rather than `serveur && !productionBuild` so the veto is a sentence
 * somebody can read rather than an operator precedence somebody can misplace.
 */
export function estDemo({ serveur, productionBuild }: DemoEntrees): boolean {
  // The veto. A production build renders no simulation device, whatever the
  // database, the restore, or the admin route says.
  if (productionBuild) return false;
  return serveur;
}

/**
 * 4.7.3's startup assertion, for the app's own boot.
 *
 * *"A startup assertion fails the boot if it is on with
 * `NODE_ENV=production`."* Canon writes it for the service; the same sentence
 * is worth the same treatment in an app, because the failure it prevents is
 * identical and the cost is a line.
 *
 * It throws rather than warning: a warning at boot is read by nobody, and the
 * state it describes , a production deployment advertising a persona switcher
 * , is one nobody would knowingly ship.
 */
export function assertDemoCoherent({ serveur, productionBuild }: DemoEntrees): void {
  if (productionBuild && serveur) {
    throw new Error(
      "boot refused: the demo flag is on in a production build. 4.7.3 allows it in local and staging only.",
    );
  }
}
